import * as z from 'zod'

import {
  UNKNOWN_AREA_ENTRY,
  isMissingBreed,
  requiresShoulderHeight,
} from '@flows/configs/petHealthV2/utils'
import { Schema_PetHealthV2GetsafeDe } from '@flows/insurance/schemas'

// Not required for pricing, so the generated schema leaves them optional, but the flow asks them
const FLOW_REQUIRED_PARAMS = ['pet_name', 'pet_gender', 'current_health_status'] as const

const pricingParamsSchema = Schema_PetHealthV2GetsafeDe.superRefine((values, ctx) => {
  if (requiresShoulderHeight(values) && values.shoulder_height_class === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.invalid_type,
      expected: 'string',
      received: 'undefined',
      message: 'Required',
      path: ['shoulder_height_class'],
    })
  }
})

const flowSchema = pricingParamsSchema.superRefine((values, ctx) => {
  FLOW_REQUIRED_PARAMS.filter((param) => values[param] === undefined).forEach((param) => {
    ctx.addIssue({
      code: z.ZodIssueCode.invalid_type,
      expected: 'string',
      received: 'undefined',
      message: 'Required',
      path: [param],
    })
  })
  if (values.health_status_entries?.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      minimum: 1,
      type: 'array',
      inclusive: true,
      message: 'Required',
      path: ['health_status_entries'],
    })
  }
})

const hasNoConditions = (entries: unknown) => !Array.isArray(entries) || entries.length === 0

const preprocessParams = (values: unknown) => {
  const valuesCopy = z.record(z.string(), z.unknown()).catch({}).parse(values)

  if (valuesCopy.breed_type === 'pedigree' || isMissingBreed(valuesCopy.second_breed_name)) {
    delete valuesCopy.second_breed_name
  }

  if (!requiresShoulderHeight(valuesCopy)) {
    delete valuesCopy.shoulder_height_class
  }

  if (valuesCopy.current_health_status === 'healthy') {
    delete valuesCopy.health_status_entries
    delete valuesCopy.health_status_acknowledged_on
  }

  if (valuesCopy.tariff_type === 'surgery_coverage') {
    valuesCopy.addon_preventive_care = false
  }

  if (valuesCopy.previous_insurance !== 'true') {
    delete valuesCopy.previous_insurance_end_on
  }

  return valuesCopy
}

export const schema = z.preprocess(preprocessParams, flowSchema)

const preprocessPricingParams = (values: unknown) => {
  const valuesCopy = preprocessParams(values)

  if (
    valuesCopy.current_health_status !== undefined &&
    valuesCopy.current_health_status !== 'healthy' &&
    hasNoConditions(valuesCopy.health_status_entries)
  ) {
    valuesCopy.health_status_entries = [UNKNOWN_AREA_ENTRY]
  }

  return valuesCopy
}

export const pricingSchema = z.preprocess(preprocessPricingParams, pricingParamsSchema)
